/**
 * AcepsleWordmark — the Acepsle word logo.
 *
 * Rebuilt as left-aligned HTML text + an inline chevron so the visible
 * mark sits flush to the left (aligns cleanly with adjacent content)
 * and uses a real loaded font. The wordmark is set in Poppins — a
 * geometric sans in the same family as Wave's brand font (Averta,
 * which is commercial); Poppins is the closest freely-available match.
 *
 * Variants:
 *   - default:  orange chevron + charcoal text (light backgrounds)
 *   - reversed: white chevron + white text (orange / dark backgrounds)
 */
const ORANGE = '#FF6B35';
const CHARCOAL = '#22272E';

interface AcepsleWordmarkProps {
  /** target text size / overall height in px */
  height?: number;
  variant?: 'default' | 'reversed';
  /** include "PROPRIETARY LIMITED" tagline beneath */
  withTagline?: boolean;
  className?: string;
}

export default function AcepsleWordmark({
  height = 28,
  variant = 'default',
  withTagline = false,
  className = '',
}: AcepsleWordmarkProps) {
  const reversed = variant === 'reversed';
  const textColor = reversed ? '#FFFFFF' : CHARCOAL;
  const chevronColor = reversed ? '#FFFFFF' : ORANGE;
  const chevW = Math.round(height * 0.52);
  const gap = Math.round(height * 0.1);

  const row = (
    <span className="inline-flex items-center" style={{ lineHeight: 1 }}>
      <svg
        width={chevW}
        height={Math.round(height * 1.0)}
        viewBox="0 0 12 24"
        fill="none"
        aria-hidden="true"
        style={{ marginRight: gap, display: 'block' }}
      >
        <path
          d="M2 5 L9 12 L2 19"
          stroke={chevronColor}
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span
        style={{
          fontFamily: "'Poppins', 'Inter', system-ui, sans-serif",
          fontWeight: 600,
          fontSize: height,
          color: textColor,
          letterSpacing: '-0.01em',
        }}
      >
        Acepsle
      </span>
    </span>
  );

  if (!withTagline) {
    return (
      <span className={className} role="img" aria-label="Acepsle">
        {row}
      </span>
    );
  }

  return (
    <span
      className={`inline-flex flex-col ${className}`}
      role="img"
      aria-label="Acepsle"
    >
      {row}
      <span
        style={{
          fontFamily: "'Poppins', 'Inter', system-ui, sans-serif",
          fontWeight: 600,
          fontSize: Math.max(8, Math.round(height * 0.24)),
          letterSpacing: '0.2em',
          color: reversed ? 'rgba(255,255,255,0.7)' : '#5B626B',
          marginTop: Math.round(height * 0.16),
          marginLeft: chevW + gap,
        }}
      >
        PROPRIETARY LIMITED
      </span>
    </span>
  );
}
